import { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { DeviceType } from 'shared/models/device-type';

import CreateLinkableTransmitterCommand from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/domain/create-linkable-transmitter-command';
import LinkableTransmitterStep from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/domain/linkable-transmitter-step';
import LinkableTransmitterStepType from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/domain/linkable-transmitter-step-type';
import useLinkableTransmitterSteps from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/states/hooks/linkable-transmitter-steps';
import useLinkableTransmitterValidationPopupState, {
  LinkableTransmitterValidationPopupState,
} from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/states/linkable-transmitter-validation-popup-state';

export type LinkableTransmitterStepsProviderState = {
  steps: LinkableTransmitterStep[];
  currentStep: LinkableTransmitterStep;
  activeStepIndex: number;
  isValidating: boolean;
  transmitterResidentSettings: DeviceType[];
  setCurrentStep: (step: LinkableTransmitterStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  handleStepClick: (targetIndex: number) => Promise<void>;
  isStepClickable: (index: number) => boolean;
  cancel: () => void;
  getIsNextStepDisabled: () => boolean;
  addTransmitter: () => void;
  form: UseFormReturn<CreateLinkableTransmitterCommand>;
  validationPopupState: LinkableTransmitterValidationPopupState;
};

type Props = {
  toggleLinkableTransmitterSteps: () => void;
  onTransmitterAdded: (createdDeviceId: string) => void;
  transmitterResidentSettings: DeviceType[];
};

const useLinkableTransmitterStepsProviderState = ({
  toggleLinkableTransmitterSteps,
  onTransmitterAdded,
  transmitterResidentSettings,
}: Props): LinkableTransmitterStepsProviderState => {
  const validationPopupState = useLinkableTransmitterValidationPopupState();

  const steps: LinkableTransmitterStep[] = [
    {
      translationKey: 'stepSelectType',
      type: LinkableTransmitterStepType.SelectType,
    },
    {
      translationKey: 'stepEnterDeviceId',
      type: LinkableTransmitterStepType.EnterDeviceId,
    },
    {
      translationKey: 'stepConfirm',
      type: LinkableTransmitterStepType.Confirm,
    },
  ];

  const form = useForm<CreateLinkableTransmitterCommand>({
    mode: 'onBlur',
    defaultValues: {
      type: DeviceType.Unknown,
      externalId: '',
    },
  });

  const { validateTransmitter, addTransmitter } = useLinkableTransmitterSteps({ form, validationPopupState, onTransmitterAdded });

  const [currentStep, setCurrentStep] = useState<LinkableTransmitterStep>(steps[0]);
  const [isValidating, setIsValidating] = useState(false);

  const activeStepIndex = steps.findIndex((step) => step.type === currentStep.type);

  const goToStep = (step: LinkableTransmitterStep) => {
    if (step.type === LinkableTransmitterStepType.SelectType) {
      form.resetField('externalId');
    }
    setCurrentStep(step);
  };

  const getIsNextStepDisabled = (): boolean => {
    if (isValidating) {
      return true;
    }

    const deviceType = form.watch('type');
    const externalId = form.watch('externalId');

    switch (currentStep.type) {
      case LinkableTransmitterStepType.SelectType:
        return deviceType === DeviceType.Unknown || !deviceType;
      case LinkableTransmitterStepType.EnterDeviceId:
        return !externalId || !form.formState.isValid;
      default:
        return false;
    }
  };

  const validateAndGoToNextStep = async (): Promise<boolean> => {
    if (isValidating || getIsNextStepDisabled()) {
      return false;
    }

    if (currentStep.type === LinkableTransmitterStepType.EnterDeviceId) {
      setIsValidating(true);
      const isValid = await validateTransmitter().finally(() => {
        setIsValidating(false);
      });
      return isValid;
    }

    return true;
  };

  const goToNextStep = async (): Promise<void> => {
    const canProceed = await validateAndGoToNextStep();
    if (canProceed && activeStepIndex < steps.length - 1) {
      goToStep(steps[activeStepIndex + 1]);
    }
  };

  const goToPreviousStep = (): void => {
    if (activeStepIndex > 0) {
      goToStep(steps[activeStepIndex - 1]);
    }
  };

  const isStepClickable = (index: number): boolean => {
    if (isValidating) {
      return false;
    }
    if (index < activeStepIndex) {
      return true;
    }
    if (index === activeStepIndex) {
      return false;
    }
    if (index === activeStepIndex + 1 && !getIsNextStepDisabled()) {
      return true;
    }
    return false;
  };

  const handleStepClick = async (targetIndex: number): Promise<void> => {
    if (isValidating || targetIndex === activeStepIndex) {
      return;
    }

    if (targetIndex < activeStepIndex) {
      goToStep(steps[targetIndex]);
      return;
    }

    if (targetIndex === activeStepIndex + 1) {
      const canProceed = await validateAndGoToNextStep();
      if (canProceed) {
        goToStep(steps[targetIndex]);
      }
    }
  };

  return {
    steps,
    currentStep,
    activeStepIndex,
    isValidating,
    transmitterResidentSettings,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    handleStepClick,
    isStepClickable,
    cancel: toggleLinkableTransmitterSteps,
    getIsNextStepDisabled,
    addTransmitter,
    form,
    validationPopupState,
  };
};

export default useLinkableTransmitterStepsProviderState;
